import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import { Add, Delete, Security, Lock } from '@mui/icons-material';
import { useUserRoles, useAssignUserRole, useRemoveUserRole } from '../hooks/useUsers';
import { useRoles } from '../hooks/useRoles';
import { useOrganizations } from '../hooks/useFleet';

interface UserRoleAssignmentProps {
  userId: string;
  userName?: string;
  isReadOnly?: boolean;
}

const scopeColors: Record<string, string> = {
  global: '#ef4444',
  organization: '#6366f1',
  resource: '#10b981',
};

const UserRoleAssignment: React.FC<UserRoleAssignmentProps> = ({
  userId,
  userName,
  isReadOnly = false,
}) => {
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    role_id: '',
    organization_id: '',
  });
  const [error, setError] = useState<string | null>(null);

  const { data: rolesData, isLoading: rolesLoading } = useUserRoles(userId);
  const { data: availableRoles } = useRoles({ size: 100, include_system: true });
  const { data: orgsData } = useOrganizations({ size: 100 });
  const assignMutation = useAssignUserRole();
  const removeMutation = useRemoveUserRole();

  const assignments = rolesData?.assignments || [];
  const effectiveRoles = rolesData?.effective_roles || [];

  const handleOpenAddDialog = () => {
    setFormData({ role_id: '', organization_id: '' });
    setError(null);
    setAddDialogOpen(true);
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    setFormData({ role_id: '', organization_id: '' });
    setError(null);
  };

  const handleOpenDeleteDialog = (assignmentId: string) => {
    setSelectedAssignment(assignmentId);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedAssignment(null);
  };

  const handleAssignRole = async () => {
    if (!formData.role_id) {
      setError('Please select a role');
      return;
    }

    const selectedRole = availableRoles?.data?.find(r => r.id === formData.role_id);
    if (selectedRole?.scope === 'organization' && !formData.organization_id) {
      setError('Organization is required for organization-scoped roles');
      return;
    }

    try {
      await assignMutation.mutateAsync({
        userId,
        data: {
          role_id: formData.role_id,
          organization_id: selectedRole?.scope === 'organization' ? formData.organization_id : undefined,
        },
      });
      handleCloseAddDialog();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to assign role');
    }
  };

  const handleRemoveRole = async () => {
    if (!selectedAssignment) return;

    try {
      await removeMutation.mutateAsync({
        userId,
        assignmentId: selectedAssignment,
      });
      handleCloseDeleteDialog();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to remove role');
    }
  };

  const selectedRole = availableRoles?.data?.find(r => r.id === formData.role_id);
  const selectedAssignmentData = assignments.find(a => a.id === selectedAssignment);

  if (rolesLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Role Assignments */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={500}>
          Role Assignments
        </Typography>
        {!isReadOnly && (
          <Button
            size="small"
            startIcon={<Add />}
            onClick={handleOpenAddDialog}
            sx={{ color: '#10b981' }}
          >
            Assign Role
          </Button>
        )}
      </Box>

      {assignments.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          No roles assigned to this user. Assign a role to grant permissions.
        </Alert>
      ) : (
        <TableContainer component={Paper} sx={{ bgcolor: '#2a2a2a', mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Role</TableCell>
                <TableCell>Scope</TableCell>
                <TableCell>Organization</TableCell>
                <TableCell>Assigned</TableCell>
                {!isReadOnly && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {assignments.map((assignment) => (
                <TableRow key={assignment.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Security sx={{ color: '#6366f1', fontSize: 18 }} />
                      <Typography variant="body2">{assignment.role_name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={assignment.role_scope}
                      size="small"
                      sx={{
                        bgcolor: scopeColors[assignment.role_scope] || '#666',
                        color: 'white',
                        textTransform: 'capitalize',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {assignment.organization_name || '-'}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(assignment.created_at).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  {!isReadOnly && (
                    <TableCell align="right">
                      <Tooltip title="Remove role">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDeleteDialog(assignment.id)}
                          sx={{ color: 'error.main' }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Effective Roles Summary */}
      {effectiveRoles.length > 0 && (
        <Box>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Effective Roles (by priority)
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {effectiveRoles
              .sort((a, b) => b.priority - a.priority)
              .map((role) => (
                <Chip
                  key={role.role_id}
                  icon={role.is_system ? <Lock sx={{ fontSize: 14 }} /> : <Security sx={{ fontSize: 14 }} />}
                  label={`${role.role_name} (${role.priority})`}
                  size="small"
                  variant="outlined"
                  sx={{
                    borderColor: scopeColors[role.scope] || '#666',
                    '& .MuiChip-icon': { color: role.is_system ? '#f59e0b' : '#6366f1' },
                  }}
                />
              ))}
          </Box>
        </Box>
      )}

      {/* Add Role Dialog */}
      <Dialog open={addDialogOpen} onClose={handleCloseAddDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Role to {userName || 'User'}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>Role</InputLabel>
              <Select
                value={formData.role_id}
                label="Role"
                onChange={(e) => setFormData({ ...formData, role_id: e.target.value })}
              >
                {(availableRoles?.data || []).map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {role.is_system ? (
                        <Lock sx={{ fontSize: 16, color: '#f59e0b' }} />
                      ) : (
                        <Security sx={{ fontSize: 16, color: '#6366f1' }} />
                      )}
                      <span>{role.name}</span>
                      <Chip
                        label={role.scope}
                        size="small"
                        sx={{
                          ml: 1,
                          height: 20,
                          bgcolor: scopeColors[role.scope] || '#666',
                          color: 'white',
                          textTransform: 'capitalize',
                        }}
                      />
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedRole?.scope === 'organization' && (
              <FormControl fullWidth required>
                <InputLabel>Organization</InputLabel>
                <Select
                  value={formData.organization_id}
                  label="Organization"
                  onChange={(e) => setFormData({ ...formData, organization_id: e.target.value })}
                >
                  {(orgsData?.data || []).map((org) => (
                    <MenuItem key={org.id} value={org.id}>
                      {org.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {selectedRole && (
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>{selectedRole.name}</strong>: {selectedRole.description || 'No description'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Priority: {selectedRole.priority} | Scope: {selectedRole.scope}
                </Typography>
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog}>Cancel</Button>
          <Button
            onClick={handleAssignRole}
            variant="contained"
            disabled={assignMutation.isPending}
            sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}
          >
            {assignMutation.isPending ? <CircularProgress size={20} /> : 'Assign Role'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Remove Role Assignment?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove the role <strong>{selectedAssignmentData?.role_name}</strong> from this user?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This will revoke all permissions granted by this role.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button
            onClick={handleRemoveRole}
            color="error"
            variant="contained"
            disabled={removeMutation.isPending}
          >
            {removeMutation.isPending ? <CircularProgress size={20} /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserRoleAssignment;
